import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { cents, modules } from "byzantine";
import { useRecipients } from "byzantine/src/dbbl/businessLogic/entities/recipients";
import filters from "byzantine/src/filters";
import { useDomesticWireImportContext } from "./DomesticWireImportContext";

export type WirePaymentEdit = {
  wire_reason: string;
  memo: string;
  amount: number; // cents
};

export type WirePaymentErrors = Partial<
  Record<keyof WirePaymentEdit | "recipient", string>
>;

export type WireSortOption = "fileOrder" | "error";

export const WIRE_SORT_LABEL_KEYS: Record<WireSortOption, string> = {
  fileOrder: "bulk-sort-file-order",
  error: "bulk-sort-error",
};

const PAGE_SIZE = 15;

/**
 * Determines whether a wire import row has any error — either from backend
 * parse validation (field_errors, row_errors) or client-side validation.
 */
const hasWireRowError = (
  row: API.WireImport.CSVParsedRow,
  clientErrors?: WirePaymentErrors
): boolean => {
  if (clientErrors && Object.keys(clientErrors).length > 0) return true;
  if (row.field_errors && Object.keys(row.field_errors).length > 0)
    return true;
  if (row.row_errors && row.row_errors.length > 0) return true;
  return false;
};

// eslint-disable-next-line import/prefer-default-export
export const useWirePaymentsTable = () => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { parsedRows, updateParsedRows } = useDomesticWireImportContext();
  const wireReasons = modules.wireTemplates.useWireReasons();

  // Filtered recipients: only those with fedwire_destination
  // unless user has manage_recipient permission (show all)
  const allRecipients = useRecipients();
  const hasManageRecipientPermission =
    modules.achPayments.useManageRecipientPermission();
  const wireRecipients = useMemo(
    () =>
      allRecipients.filter(
        (r) =>
          !r.hidden &&
          (hasManageRecipientPermission || !!r.fedwire_destination)
      ),
    [allRecipients, hasManageRecipientPermission]
  );

  const rows = useMemo(() => parsedRows ?? [], [parsedRows]);

  // --- Editable row state ---
  const [rowEdits, setRowEdits] = useState<
    Record<number, WirePaymentEdit>
  >(() => {
    const edits: Record<number, WirePaymentEdit> = {};
    rows.forEach((row, idx) => {
      edits[idx] = {
        wire_reason: row.wire_reason ?? "",
        memo: row.memo ?? "",
        amount: Math.round((row.amount ?? 0) * 100),
      };
    });
    return edits;
  });

  // --- Validation errors ---
  const [rowErrors, setRowErrors] = useState<
    Record<number, WirePaymentErrors>
  >({});
  const [clickedNext, setClickedNext] = useState(false);

  // --- Selection / checkbox state ---
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [checkboxHeaderSelected, setCheckboxHeaderSelected] =
    useState(false);

  const toggleRowSelection = useCallback((index: number) => {
    setSelectedRows((prev) =>
      prev.includes(index)
        ? prev.filter((i) => i !== index)
        : [...prev, index]
    );
  }, []);

  const handleToggleCheckboxHeader = useCallback(() => {
    if (checkboxHeaderSelected) {
      setSelectedRows([]);
    } else {
      setSelectedRows(rows.map((_, idx) => idx));
    }
    setCheckboxHeaderSelected((prev) => !prev);
  }, [checkboxHeaderSelected, rows]);

  const handleClearRowsSelection = useCallback(() => {
    setSelectedRows([]);
    setCheckboxHeaderSelected(false);
  }, []);

  const handleRemoveRows = useCallback(() => {
    const removeSet = new Set(selectedRows);

    // Remove from context parsedRows
    updateParsedRows((prev) =>
      prev.filter((_, idx) => !removeSet.has(idx))
    );

    // Rebuild rowEdits with re-indexed keys
    setRowEdits((prev) => {
      const newEdits: Record<number, WirePaymentEdit> = {};
      let newIdx = 0;
      Object.keys(prev)
        .map(Number)
        .sort((a, b) => a - b)
        .forEach((oldIdx) => {
          if (!removeSet.has(oldIdx)) {
            newEdits[newIdx] = prev[oldIdx];
            newIdx += 1;
          }
        });
      return newEdits;
    });

    // Rebuild rowErrors with re-indexed keys (iterate all row indices, not just error keys)
    setRowErrors((prev) => {
      const newErrors: Record<number, WirePaymentErrors> = {};
      let newIdx = 0;
      const allIndices = Object.keys(rowEdits)
        .map(Number)
        .sort((a, b) => a - b);
      allIndices.forEach((oldIdx) => {
        if (!removeSet.has(oldIdx)) {
          if (prev[oldIdx]) {
            newErrors[newIdx] = prev[oldIdx];
          }
          newIdx += 1;
        }
      });
      return newErrors;
    });

    setSelectedRows([]);
    setCheckboxHeaderSelected(false);
  }, [selectedRows, updateParsedRows, rowEdits]);

  const selectedTotal = useMemo(
    () =>
      filters.currency(
        cents(
          selectedRows.reduce(
            (sum, idx) => sum + (rowEdits[idx]?.amount || 0),
            0
          )
        )
      ),
    [selectedRows, rowEdits]
  );

  const [sortBy, setSortBy] = useState<WireSortOption>("fileOrder");
  const [sortPopoverOpen, setSortPopoverOpen] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(rows.length / PAGE_SIZE);

  // Reset page when sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [sortBy]);

  // Clamp to last page if current page is out of bounds (e.g., rows removed)
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const displayedRows = useMemo(() => {
    const indexed = rows.map((row, i) => ({
      row,
      originalIndex: i,
    }));

    let sorted = indexed;
    if (sortBy === "error") {
      // Precompute error rank once per row to avoid repeated
      // Object.keys() work inside the sort comparator.
      const errorRank = rows.map((row, i) =>
        hasWireRowError(row, rowErrors[i]) ? 0 : 1
      );
      sorted = [...indexed].sort((a, b) => {
        const diff = errorRank[a.originalIndex] - errorRank[b.originalIndex];
        return diff !== 0 ? diff : a.originalIndex - b.originalIndex;
      });
    }

    const start = (currentPage - 1) * PAGE_SIZE;
    return sorted.slice(start, start + PAGE_SIZE);
  }, [rows, currentPage, sortBy, rowErrors]);

  // --- Totals ---
  const totalAmount = useMemo(
    () =>
      Object.values(rowEdits).reduce(
        (sum, edit) => sum + (edit.amount || 0),
        0
      ),
    [rowEdits]
  );

  const formattedTotal = useMemo(
    () => filters.currency(cents(totalAmount)),
    [totalAmount]
  );

  // --- Field change handlers ---
  const handleFieldChange = useCallback(
    (idx: number, field: keyof WirePaymentEdit, value: string | number) => {
      setRowEdits((prev) => ({
        ...prev,
        [idx]: { ...prev[idx], [field]: value },
      }));

      // Clear error on change if user has clicked next
      if (clickedNext) {
        setRowErrors((prev) => {
          const rowErr = { ...prev[idx] };
          delete rowErr[field];
          if (Object.keys(rowErr).length === 0) {
            const next = { ...prev };
            delete next[idx];
            return next;
          }
          return { ...prev, [idx]: rowErr };
        });
      }
    },
    [clickedNext]
  );

  // --- Recipient selection ---
  const handleSelectRecipient = useCallback(
    (rowIndex: number, recipient: API.Recipient) => {
      updateParsedRows((prev) =>
        prev.map((row, idx) =>
          idx === rowIndex ? { ...row, recipient } : row
        )
      );

      // Clear recipient error if user has clicked next
      if (clickedNext) {
        setRowErrors((prev) => {
          const rowErr = { ...prev[rowIndex] };
          delete rowErr.recipient;
          if (Object.keys(rowErr).length === 0) {
            const next = { ...prev };
            delete next[rowIndex];
            return next;
          }
          return { ...prev, [rowIndex]: rowErr };
        });
      }
    },
    [updateParsedRows, clickedNext]
  );

  const handleClearRecipient = useCallback(
    (rowIndex: number) => {
      updateParsedRows((prev) =>
        prev.map((row, idx) =>
          idx === rowIndex ? { ...row, recipient: null } : row
        )
      );
    },
    [updateParsedRows]
  );

  // --- Validation ---
  const validateAll = useCallback((): boolean => {
    const errors: Record<number, WirePaymentErrors> = {};
    let valid = true;

    rows.forEach((row, idx) => {
      const edit = rowEdits[idx];
      if (!edit) return;

      const rowErr: WirePaymentErrors = {};
      if (!row.recipient?.id) {
        rowErr.recipient = l10n.getString(
          "wire-payments-table-error-recipient-required"
        );
      }
      if (wireReasons.length > 0 && !edit.wire_reason.trim()) {
        rowErr.wire_reason = l10n.getString(
          "wire-payments-table-error-reason-required"
        );
      }
      if (!edit.memo.trim()) {
        rowErr.memo = l10n.getString(
          "wire-payments-table-error-memo-required"
        );
      }
      if (!edit.amount || edit.amount <= 0) {
        rowErr.amount = l10n.getString(
          "wire-payments-table-error-amount-required"
        );
      }

      if (Object.keys(rowErr).length > 0) {
        errors[idx] = rowErr;
        valid = false;
      }
    });

    setRowErrors(errors);
    return valid;
  }, [rows, rowEdits, l10n, wireReasons]);

  // --- Add row ---
  const handleAddRow = useCallback(() => {
    const blankRow: API.WireImport.CSVParsedRow = {
      row_index: rows.length,
      recipient: null,
      field_errors: null,
      amount: null,
      memo: null,
      wire_reason: null,
      recipient_name: null,
      routing_number: null,
      account_number: null,
      recipient_street_address: null,
      recipient_city: null,
      recipient_region_code: null,
      recipient_postal_code: null,
      needs_review: false,
      warning: "",
      row_errors: [],
    };

    updateParsedRows((prev) => [...prev, blankRow]);

    const newIndex = rows.length;
    setRowEdits((prev) => ({
      ...prev,
      [newIndex]: { wire_reason: "", memo: "", amount: 0 },
    }));

    // Jump to the last page so the new row is visible
    const newTotalPages = Math.ceil((rows.length + 1) / PAGE_SIZE);
    setCurrentPage(newTotalPages);
  }, [rows.length, updateParsedRows]);

  // --- Navigation ---
  const handleBack = useCallback(() => {
    navigate("../payment-details");
  }, [navigate]);

  const handleNext = useCallback(() => {
    setClickedNext(true);
    if (!validateAll()) return;

    // Sync edits back to context parsedRows
    updateParsedRows((prev) =>
      prev.map((row, idx) => {
        const edit = rowEdits[idx];
        if (!edit) return row;
        return {
          ...row,
          wire_reason: edit.wire_reason,
          memo: edit.memo,
          amount: edit.amount / 100, // convert cents back to dollars
        };
      })
    );

    navigate("../review");
  }, [validateAll, rowEdits, updateParsedRows, navigate]);

  return {
    hasParsedRows: parsedRows !== null,
    rows,
    displayedRows,
    rowEdits,
    rowErrors,
    clickedNext,
    wireReasons,
    wireRecipients,
    currentPage,
    totalPages,
    totalAmount,
    formattedTotal,
    setCurrentPage,
    handleFieldChange,
    handleSelectRecipient,
    handleClearRecipient,
    handleAddRow,
    handleBack,
    handleNext,
    selectedRows,
    selectedTotal,
    checkboxHeaderSelected,
    toggleRowSelection,
    handleToggleCheckboxHeader,
    handleClearRowsSelection,
    handleRemoveRows,
    PAGE_SIZE,
    sortBy,
    setSortBy,
    sortPopoverOpen,
    setSortPopoverOpen,
  };
};
